import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { auth, db } from '../../firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { IconShieldCheck, IconCheck } from '@tabler/icons-react';

export default function InvitationAcceptance() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const q = query(
          collection(db, 'organisations',
            // We search across all orgs for this invite token
            // token is the invite doc ID
          ),
        );

        // Search all orgs for invite with this ID
        const orgsSnap = await getDocs(collection(db, 'organisations'));
        let foundInvite = null;
        let foundOrgId = null;

        for (const orgDoc of orgsSnap.docs) {
          const inviteDoc = await getDocs(
            query(
              collection(db, 'organisations', orgDoc.id, 'invites'),
              where('__name__', '==', token)
            )
          );
          if (!inviteDoc.empty) {
            foundInvite = { id: inviteDoc.docs[0].id, ...inviteDoc.docs[0].data() };
            foundOrgId = orgDoc.id;
            break;
          }
        }

        if (!foundInvite || foundInvite.accepted) {
          setInvalid(true);
        } else {
          const expiresAt = foundInvite.expiresAt?.toDate
            ? foundInvite.expiresAt.toDate()
            : new Date(foundInvite.expiresAt);
          if (expiresAt < new Date()) {
            setInvalid(true);
          } else {
            setInvite({ ...foundInvite, orgId: foundOrgId });
          }
        }
      } catch (err) {
        console.error(err);
        setInvalid(true);
      }
      setLoading(false);
    };
    load();
  }, [token]);

  const handleAccept = async () => {
    if (!password || password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setSaving(true);
    setError('');
    try {
      // Create Firebase Auth account
      const { user } = await createUserWithEmailAndPassword(
        auth,
        invite.email,
        password
      );

      // Create user profile in Firestore
      await setDoc(
        doc(db, 'organisations', invite.orgId, 'users', user.uid),
        {
          name: `${invite.firstName} ${invite.lastName}`.trim(),
          email: invite.email,
          role: invite.role,
          orgId: invite.orgId,
          siteId: invite.siteId || null,
          siteName: invite.siteName || null,
          photoURL: null,
          onboardingComplete: false,
          invitedBy: invite.invitedById || null,
          invitedByName: invite.invitedByName || null,
          createdAt: serverTimestamp(),
        }
      );

      // Mark invite as accepted
      await updateDoc(
        doc(db, 'organisations', invite.orgId, 'invites', invite.id),
        {
          accepted: true,
          acceptedAt: serverTimestamp(),
          acceptedBy: user.uid,
        }
      );

      // Navigate to correct onboarding
      if (invite.role === 'org_admin') navigate('/onboarding/admin');
      else if (invite.role === 'manager') navigate('/onboarding/manager');
      else navigate('/onboarding/operator');

    } catch (err) {
      console.error(err);
      if (err.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists — please sign in instead');
      } else {
        setError('Failed to create account — please try again');
      }
    }
    setSaving(false);
  };

  if (loading) return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--color-bg-secondary)',
      fontSize: 13,
      color: 'var(--color-text-tertiary)',
    }}>
      Loading invitation...
    </div>
  );

  if (invalid) return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--color-bg-secondary)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 16,
    }}>
      <div style={{
        background: 'var(--color-bg-primary)',
        border: '0.5px solid var(--color-border-light)',
        borderRadius: 'var(--radius-xl)',
        padding: '40px 32px',
        textAlign: 'center',
        maxWidth: 400,
        width: '100%',
      }}>
        <div style={{ fontSize: 16, fontWeight: 400, marginBottom: 8 }}>
          Invalid invitation
        </div>
        <div style={{
          fontSize: 13,
          color: 'var(--color-text-tertiary)',
          lineHeight: 1.6,
          marginBottom: 24,
        }}>
          This invitation link is invalid, has expired, or has already been used.
          Please contact your manager for a new invitation.
        </div>
        <button
          onClick={() => navigate('/login')}
          style={{
            background: 'var(--color-brand)',
            color: '#fff',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            padding: '10px 24px',
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          Go to sign in
        </button>
      </div>
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--color-bg-secondary)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 16,
    }}>
      <div style={{ width: '100%', maxWidth: 440 }}>

        <div style={{
          background: 'var(--color-brand)',
          borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0',
          padding: '28px 28px 24px',
          textAlign: 'center',
        }}>
          <div style={{
            width: 48,
            height: 48,
            background: 'rgba(255,255,255,0.2)',
            borderRadius: 'var(--radius-lg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 12px',
          }}>
            <IconShieldCheck size={26} color="#fff" stroke={1.5} />
          </div>
          <div style={{ fontSize: 20, fontWeight: 400, color: '#fff', marginBottom: 2 }}>
            You've been invited
          </div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)' }}>
            {invite?.orgName} · Agriware Ops
          </div>
        </div>

        <div style={{
          background: 'var(--color-bg-primary)',
          borderRadius: '0 0 var(--radius-xl) var(--radius-xl)',
          border: '0.5px solid var(--color-border-light)',
          borderTop: 'none',
          padding: '28px',
        }}>

          {/* Invite details */}
          <div style={{
            background: 'var(--color-bg-secondary)',
            borderRadius: 'var(--radius-md)',
            padding: '12px 16px',
            marginBottom: 20,
          }}>
            <div style={{ fontSize: 13, color: 'var(--color-text-primary)', marginBottom: 4 }}>
              {invite?.firstName} {invite?.lastName}
            </div>
            <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
              {invite?.email} · {invite?.role?.replace('_', ' ')}
              {invite?.siteName ? ` · ${invite.siteName}` : ''}
            </div>
            {invite?.invitedByName && (
              <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 4 }}>
                Invited by {invite.invitedByName}
              </div>
            )}
          </div>

          {error && (
            <div style={{
              background: 'var(--color-danger-light)',
              border: '0.5px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              padding: '10px 14px',
              fontSize: 13,
              color: 'var(--color-danger)',
              marginBottom: 16,
            }}>
              {error}
            </div>
          )}

          <div style={{ marginBottom: 12 }}>
            <label style={{
              display: 'block',
              fontSize: 11,
              color: 'var(--color-text-secondary)',
              marginBottom: 5,
            }}>
              Create a password
            </label>
            <input
              type="password"
              placeholder="At least 8 characters"
              value={password}
              onChange={e => setPassword(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={{
              display: 'block',
              fontSize: 11,
              color: 'var(--color-text-secondary)',
              marginBottom: 5,
            }}>
              Confirm password
            </label>
            <input
              type="password"
              placeholder="Repeat your password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAccept()}
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
          </div>

          <button
            onClick={handleAccept}
            disabled={saving}
            style={{
              width: '100%',
              background: 'var(--color-brand)',
              color: '#fff',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              padding: '11px',
              fontSize: 14,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            {saving ? 'Setting up your account...' : (
              <><IconCheck size={16} stroke={1.5} /> Accept invitation</>
            )}
          </button>

          <div style={{
            textAlign: 'center',
            fontSize: 11,
            color: 'var(--color-text-tertiary)',
            marginTop: 20,
          }}>
            Already have an account?{' '}
            <span
              onClick={() => navigate('/login')}
              style={{ color: 'var(--color-brand)', cursor: 'pointer' }}
            >
              Sign in
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}